// ─────────────────────────────────────────────────────────────────────────────
// useViewport
//
// One source of truth for "what size screen am I on right now?" Subscribes to
// window.resize and returns the current width plus boolean flags for the three
// breakpoints used across the app:
//
//   isMobile   — < 640px  (phones; iPhone SE through Pro Max in portrait)
//   isTablet   — 640..1024 (tablets, large phones in landscape)
//   isDesktop  — >= 1024  (everything else; the design's "native" target)
//
// USAGE:
//   const { isMobile, isTablet, isDesktop, width } = useViewport();
//   <div style={{ padding: isMobile ? 12 : 20 }}>...</div>
//
// Why this and not CSS media queries:
//   The codebase uses inline `style={{}}` everywhere (no Tailwind, no CSS-in-JS
//   framework, no external stylesheets per component). Media queries can't live
//   inside inline styles. This hook puts layout branching next to the rest of
//   the JSX where it's visible. Grep for "isMobile" to find every responsive
//   decision in the app.
//
// SSR-safety:
//   typeof window === "undefined" returns desktop defaults. The component will
//   re-render on mount with real values. Since CRA isn't SSR, this is just
//   defensive.
//
// Resize debouncing:
//   We use requestAnimationFrame to coalesce rapid resize events. Without this,
//   dragging a browser window from 400px to 1400px fires ~50 setState calls.
//   With it, we get one per frame. Cheap and effective.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";

// Breakpoints — keep in sync with the @media queries in App.js (768px)
const BP_MOBILE = 768;
const BP_TABLET = 1024;

function readViewport() {
  if (typeof window === "undefined") {
    return { width: 1280, height: 800 };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

export function useViewport() {
  const [vp, setVp] = useState(readViewport);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let frame = null;
    const onResize = () => {
      if (frame != null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setVp(readViewport());
      });
    };

    window.addEventListener("resize", onResize, { passive: true });
    // Fire once on mount in case anything resized between initial state
    // computation and listener attachment.
    setVp(readViewport());

    return () => {
      window.removeEventListener("resize", onResize);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, []);

  return {
    width:     vp.width,
    height:    vp.height,
    isMobile:  vp.width < BP_MOBILE,
    isTablet:  vp.width >= BP_MOBILE && vp.width < BP_TABLET,
    isDesktop: vp.width >= BP_TABLET,
  };
}

// Exported for component-internal code that needs to reference the breakpoint
// constants directly (e.g., one-off calculations not tied to current viewport).
export const BREAKPOINTS = {
  mobile: BP_MOBILE,
  tablet: BP_TABLET,
};
